import React, { useState } from 'react';
import { apiClient } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const NewPatientForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    mobile: '',
    address: '',
    birthday: '',
    sex: '',
    emergency_contact: '',
    tag: 'regular',
    notes: '',
    opt_out_messages: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.full_name || !form.mobile) {
      toast.error('Name and mobile number are required');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/patients', form);
      toast.success('Patient created successfully');
      onSuccess?.();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create patient';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Full Name *</Label>
          <Input
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            placeholder="Patient's full name"
            data-testid="patient-name-input"
          />
        </div>
        
        <div>
          <Label>Mobile Number *</Label>
          <Input
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            placeholder="09XX XXX XXXX"
            data-testid="patient-mobile-input"
          />
        </div>
        
        <div>
          <Label>Birthday</Label>
          <Input
            type="date"
            value={form.birthday}
            onChange={(e) => setForm({ ...form, birthday: e.target.value })}
            data-testid="patient-birthday-input"
          />
        </div>
        
        <div>
          <Label>Sex</Label>
          <Select value={form.sex} onValueChange={(v) => setForm({ ...form, sex: v })}>
            <SelectTrigger data-testid="patient-sex-select">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="M">Male</SelectItem>
              <SelectItem value="F">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Tag</Label>
          <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
            <SelectTrigger data-testid="patient-tag-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="pro_bono">Pro Bono</SelectItem>
              <SelectItem value="discount">Doctor's Discount</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="col-span-2">
          <Label>Address</Label>
          <Input
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Full address"
          />
        </div>
        
        <div className="col-span-2">
          <Label>Emergency Contact</Label>
          <Input
            value={form.emergency_contact}
            onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })}
            placeholder="Name and number"
          />
        </div>
        
        <div className="col-span-2">
          <Label>Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Any notes about this patient..."
            rows={2}
          />
        </div>
        
        <div className="col-span-2 flex items-center justify-between">
          <div>
            <Label className="mb-0">Opt out of messages</Label>
            <p className="text-xs text-muted-foreground">Patient won't receive follow-up messages</p>
          </div>
          <Switch
            checked={form.opt_out_messages}
            onCheckedChange={(v) => setForm({ ...form, opt_out_messages: v })}
          />
        </div>
      </div>
      
      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" disabled={loading} data-testid="submit-patient-btn">
          {loading ? 'Creating...' : 'Add Patient'}
        </Button>
      </div>
    </form>
  );
};

export default NewPatientForm;
